import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Loader2, Package, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CHUNK_SIZE = 3000;

export default function ItemMasterPage() {
  const [count, setCount] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchCount = async () => {
    try {
      const res = await api.get("/item-master/count");
      setCount(res.data.count);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchCount(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setProgress("Reading file...");

    try {
      const text = await file.text();
      const lines = text.split("\n");
      const header = lines[0];
      const dataLines = lines.slice(1).filter((l) => l.trim());
      const totalRows = dataLines.length;
      const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
      let processed = 0;
      let designsSynced = 0;

      for (let i = 0; i < totalChunks; i++) {
        const chunk = dataLines.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const csvChunk = header + "\n" + chunk.join("\n");
        const blob = new Blob([csvChunk], { type: "text/csv" });
        const formData = new FormData();
        formData.append("file", blob, "chunk.csv");

        setProgress(`Uploading batch ${i + 1} of ${totalChunks} (${Math.min((i + 1) * CHUNK_SIZE, totalRows).toLocaleString()} / ${totalRows.toLocaleString()} rows)...`);

        const res = await api.post("/item-master/upload-csv", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
        });
        processed += chunk.length;
        designsSynced += res.data?.designs_synced || 0;
      }

      const syncMsg = designsSynced > 0 ? ` — synced WSP/MRP on ${designsSynced} tagged design${designsSynced === 1 ? "" : "s"} (history saved).` : "";
      toast.success(`Imported ${processed.toLocaleString()} items in ${totalChunks} batches${syncMsg}`);
      fetchCount();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      setProgress("");
      e.target.value = "";
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete all items from the Item Master? This cannot be undone.")) return;
    try {
      const res = await api.delete("/item-master/all");
      toast.success(`Deleted ${res.data.deleted?.toLocaleString()} items`);
      fetchCount();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="item-master-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
          Item Master
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          Upload and manage the item master for barcode tagging and WSP/MRP auto-fill.
        </p>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg p-6 max-w-lg space-y-6">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-lg bg-orange-50 flex items-center justify-center">
            <Package className="h-6 w-6 text-[#C2410C]" />
          </div>
          <div>
            <p className="text-sm text-stone-500">Total Items in Master</p>
            <p className="text-2xl font-bold text-stone-900 font-mono">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (count ?? 0).toLocaleString()}
            </p>
          </div>
        </div>

        <div className="border-t border-stone-100 pt-4">
          <p className="text-sm font-medium text-stone-700 mb-2">Upload CSV</p>
          <p className="text-xs text-stone-500 mb-3">
            CSV must have columns: barcode, design_name, shade, size, description, wsp, mrp, category4.
            Large files are uploaded in batches of {CHUNK_SIZE.toLocaleString()} rows. Existing barcodes will be updated.
          </p>
          <label className="cursor-pointer">
            <Input
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
              data-testid="item-master-file-input"
            />
            <Button
              data-testid="item-master-upload-button"
              className="bg-[#C2410C] hover:bg-[#9A3412] text-white"
              disabled={uploading}
              asChild
            >
              <span>
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                {uploading ? "Uploading..." : "Upload Item Master CSV"}
              </span>
            </Button>
          </label>
          {progress && (
            <p className="text-xs text-[#C2410C] mt-2 font-medium" data-testid="upload-progress">{progress}</p>
          )}
        </div>

        {count > 0 && (
          <div className="border-t border-stone-100 pt-4">
            <Button
              data-testid="item-master-delete-button"
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={handleDelete}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete All Items ({(count ?? 0).toLocaleString()})
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
